// "Genesis Degens" PFP traits. Seeded: seed -> traits -> pixels. Save seed + traits only.
// Rarity score = sum of -log(prob) per trait; tiers come from the exact score distribution
// over every possible combination, so editing a weight table keeps tiers honest.

import { Rng } from "../rng";
import type { RarityTier } from "../player";

export const COLLECTION_NAME = "Genesis Degens";
export const COLLECTION_SUPPLY = 10_000;

export const PFP_LAYERS = {
  background: {
    sky: 11, bubblegum: 11, mint: 11, banana: 11, lavender: 11, seafoam: 11, coral: 11, concrete: 11,
    sunset: 3, matrix: 3, // rare gradients
  },
  skin: { porcelain: 15, sand: 15, honey: 15, caramel: 15, cocoa: 15, espresso: 15, zombie: 5, gold: 2 },
  eyes: { normal: 40, sleepy: 15, wink: 12, glasses3d: 10, shades: 18, laser: 5 },
  mouth: { smile: 35, smirk: 25, frown: 15, cigar: 18, gm: 7 },
  headwear: { none: 30, cap: 15, backcap: 13, beanie: 13, bucket: 12, hoodie: 15, crown: 2 },
  accessory: { none: 40, chain: 18, earring: 15, mask: 10, headset: 12, diamond: 5 },
} as const;

export type PfpLayer = keyof typeof PFP_LAYERS;
export const LAYER_ORDER: PfpLayer[] = ["background", "skin", "eyes", "mouth", "headwear", "accessory"];
export type PfpTraits = { [L in PfpLayer]: keyof (typeof PFP_LAYERS)[L] & string };

/** Share of all supply that is rarer than each tier boundary (top-down). */
const TIER_CUTOFFS: { tier: RarityTier; mass: number }[] = [
  { tier: "Legendary", mass: 0.02 },
  { tier: "Epic", mass: 0.08 },
  { tier: "Rare", mass: 0.2 },
  { tier: "Uncommon", mass: 0.5 },
  { tier: "Common", mass: 1 },
];

export const TIER_ORDER: RarityTier[] = ["Common", "Uncommon", "Rare", "Epic", "Legendary"];

function prob(layer: PfpLayer, option: string): number {
  const table = PFP_LAYERS[layer] as Record<string, number>;
  let total = 0;
  for (const k in table) total += table[k];
  return table[option] / total;
}

export function traitProbability(layer: PfpLayer, option: string): number {
  return prob(layer, option);
}

export function rarityScore(traits: PfpTraits): number {
  let s = 0;
  for (const l of LAYER_ORDER) s -= Math.log(prob(l, traits[l]));
  return s;
}

export function generatePfpTraits(seed: number): PfpTraits {
  const rng = Rng.fromSeed(seed ^ 0x5eed_9fb1);
  const out = {} as Record<PfpLayer, string>;
  for (const l of LAYER_ORDER) out[l] = rng.weighted(PFP_LAYERS[l] as Record<string, number>);
  return out as PfpTraits;
}

// ---------- exact score distribution (computed once, lazily) ----------

interface Dist {
  scores: Float64Array; // sorted descending (rarest first)
  massAbove: Float64Array; // probability mass strictly rarer than scores[i]
}
let dist: Dist | null = null;

function distribution(): Dist {
  if (dist) return dist;
  const layers = LAYER_ORDER.map((l) => Object.keys(PFP_LAYERS[l]).map((k) => prob(l, k)));
  const combos: { s: number; p: number }[] = [];
  const walk = (i: number, s: number, p: number) => {
    if (i === layers.length) {
      combos.push({ s, p });
      return;
    }
    for (const q of layers[i]) walk(i + 1, s - Math.log(q), p * q);
  };
  walk(0, 0, 1);
  combos.sort((a, b) => b.s - a.s);
  const scores = new Float64Array(combos.length);
  const massAbove = new Float64Array(combos.length);
  let acc = 0;
  for (let i = 0; i < combos.length; i++) {
    // Ties share the mass above the first of them.
    if (i > 0 && combos[i].s === combos[i - 1].s) massAbove[i] = massAbove[i - 1];
    else massAbove[i] = acc;
    scores[i] = combos[i].s;
    acc += combos[i].p;
  }
  dist = { scores, massAbove };
  return dist;
}

/** Probability mass of all combos strictly rarer than this score. */
function massRarerThan(score: number): number {
  const { scores, massAbove } = distribution();
  // binary search in descending array for first index with scores[i] <= score
  let lo = 0;
  let hi = scores.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (scores[mid] > score + 1e-9) lo = mid + 1;
    else hi = mid;
  }
  return massAbove[lo];
}

export interface RarityInfo {
  score: number;
  tier: RarityTier;
  /** Rarity rank within the 10,000 supply (1 = rarest). */
  rank: number;
}

export function rarityOf(traits: PfpTraits): RarityInfo {
  const score = rarityScore(traits);
  const above = massRarerThan(score);
  const tier = TIER_CUTOFFS.find((c) => above < c.mass)!.tier;
  return { score, tier, rank: Math.min(COLLECTION_SUPPLY, 1 + Math.floor(above * COLLECTION_SUPPLY)) };
}

export function tokenNumber(seed: number): number {
  return 1 + ((seed >>> 0) % COLLECTION_SUPPLY);
}

/** Human-readable trait names. */
export const TRAIT_LABELS: Record<string, string> = {
  glasses3d: "3D glasses", laser: "Laser eyes", gm: "\"gm\" bubble", backcap: "Backwards cap",
  bucket: "Bucket hat", diamond: "Diamond chain", none: "None",
};

export function traitLabel(option: string): string {
  return TRAIT_LABELS[option] ?? option.charAt(0).toUpperCase() + option.slice(1);
}
